import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Table,
  Form,
  InputNumber,
  Space,
  App,
  DatePicker,
  Spin,
  Alert,
  Drawer,
  Badge,
} from 'antd';
import { EditOutlined, CloseOutlined, SaveOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { ApiPropertyAreaSimple, ApiAreaTarget, ApiUserTarget } from '../../types/rencana';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

// --- Editable Cell Component ---
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  record: any;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  record,
  children,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={[record.key, dataIndex]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: `Mohon isi ${title}!` }]}
        >
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --- Main Page Component ---
const TargetSalesPage: React.FC = () => {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [currentArea, setCurrentArea] = useState<ApiPropertyAreaSimple | null>(null);
  const [areaTarget, setAreaTarget] = useState<ApiAreaTarget | null>(null);
  const [userTargets, setUserTargets] = useState<ApiUserTarget[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());

  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(dayjs());

  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canEdit = hasPermission('targetSales.update');

  // --- Drawer States ---
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeUser, setActiveUser] = useState<ApiUserTarget | null>(null);
  const [drawerForm] = Form.useForm();
  const [totalAllocated, setTotalAllocated] = useState(0);

  useEffect(() => {
    const fetchUserArea = async () => {
      setLoading(true);
      try {
        const response = await api.get('/property-areas/role');
        const areas = response.data.data || [];
        if (areas.length > 0) {
          setCurrentArea(areas[0]); // Automatically select the first assigned area
        } else {
           message.warning("Akun Anda belum ditugaskan ke area properti manapun.");
        }
      } catch (error) {
        message.error("Gagal memuat data area properti Anda.");
      } finally {
        setLoading(false);
      }
    };
    fetchUserArea();
  }, [message]);

  const fetchData = useCallback(async (year: number, areaId: string) => {
    setLoading(true);
    form.resetFields();
    setAreaTarget(null);
    setUserTargets([]);
    try {
      const response = await api.get('/targets/user', {
        params: { periode: year, property_area_id: areaId },
      });
      const { area_target, user_agents_targets } = response.data.data;
      setAreaTarget(area_target);
      
      const processedTargets = user_agents_targets.map((u: any) => ({
        ...u,
        key: u.user_id,
        monthly_targets: u.monthly_targets || [],
      }));
      setUserTargets(processedTargets);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("Gagal memuat data target sales.");
    } finally {
      setLoading(false);
    }
  }, [message, form]);

  useEffect(() => {
    if (selectedYear && currentArea) {
      fetchData(selectedYear.year(), currentArea.id);
    }
  }, [selectedYear, currentArea, fetchData]);

  useEffect(() => {
    const formValues = userTargets.reduce((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {} as any);
    form.setFieldsValue(formValues);
  }, [userTargets, form]);

  const isEditing = (record: any) => editingKeys.has(record.key);

  const toggleEdit = (key: string) => {
    const newEditingKeys = new Set(editingKeys);
    if (newEditingKeys.has(key)) {
      newEditingKeys.delete(key);
      // Saat batal, reset field form ke nilai aslinya dari state
      const originalRecord = userTargets.find(item => item.key === key);
      if (originalRecord) {
        form.setFieldsValue({ [key]: originalRecord });
      }
    } else {
      newEditingKeys.add(key);
      const record = userTargets.find(item => item.key === key);
      form.setFieldsValue({ [key]: record });
    }
    setEditingKeys(newEditingKeys);
  };

  const totalLeadsAssigned = useMemo(() => {
    return userTargets.reduce((sum, item) => {
      const currentLeads = formValues?.[item.key]?.leads_target ?? item.leads_target;
      return sum + (currentLeads || 0);
    }, 0);
  }, [userTargets, formValues]);

  // --- Drawer Functions ---
  const openMonthlyDrawer = (record: ApiUserTarget) => {
    setActiveUser(record);
    setDrawerVisible(true);
    
    // Populate drawer form
    const formValues: Record<string, number> = {};
    const monthlyList = record.monthly_targets || [];
    
    for (let m = 1; m <= 12; m++) {
      const match = monthlyList.find(item => item.month === m);
      formValues[`month_${m}`] = match ? match.leads_target : 0;
    }
    
    drawerForm.setFieldsValue(formValues);
    
    // Calculate initial allocated total
    const sum = Object.values(formValues).reduce((acc, curr) => Number(acc) + Number(curr), 0);
    setTotalAllocated(sum);
  };

  const handleAutoDivideAll = () => {
    if (!activeUser) return;
    const annualTarget = activeUser.leads_target;
    const baseVal = Math.floor(annualTarget / 12);
    const remainder = annualTarget % 12;
    const newMonthly: Record<string, number> = {};
    for (let m = 1; m <= 12; m++) {
      newMonthly[`month_${m}`] = m <= remainder ? baseVal + 1 : baseVal;
    }
    drawerForm.setFieldsValue(newMonthly);
    setTotalAllocated(annualTarget);
  };

  const handleAutoDivideRemaining = () => {
    if (!activeUser) return;
    const annualTarget = activeUser.leads_target;
    const currentMonth = dayjs().month() + 1; // 1-12
    const remainingCount = 12 - currentMonth + 1;
    const newMonthly: Record<string, number> = {};
    
    if (remainingCount <= 0) {
      for (let m = 1; m <= 12; m++) {
        newMonthly[`month_${m}`] = m === 12 ? annualTarget : 0;
      }
    } else {
      const baseVal = Math.floor(annualTarget / remainingCount);
      const remainder = annualTarget % remainingCount;
      for (let m = 1; m <= 12; m++) {
        if (m < currentMonth) {
          newMonthly[`month_${m}`] = 0;
        } else {
          const remainingIndex = m - currentMonth + 1;
          newMonthly[`month_${m}`] = remainingIndex <= remainder ? baseVal + 1 : baseVal;
        }
      }
    }
    drawerForm.setFieldsValue(newMonthly);
    setTotalAllocated(annualTarget);
  };

  const handleSaveMonthly = async () => {
    if (!activeUser || !areaTarget?.target_id) return;
    setSaving(true);
    try {
      const formValues = await drawerForm.validateFields();
      
      const monthly_targets = Array.from({ length: 12 }, (_, i) => {
        const m = i + 1;
        return {
          month: m,
          leads_target: Number(formValues[`month_${m}`] || 0),
        };
      });
      
      const payload = [{
        target_id: activeUser.target_id || "",
        user_id: activeUser.user_id,
        property_area_target_id: areaTarget.target_id,
        leads_target: activeUser.leads_target,
        monthly_targets,
      }];
      
      const response = await api.post('/targets/user', payload);
      
      if (response.data?.data?.warning_message) {
        message.warning(response.data.data.warning_message);
      } else {
        message.success(`Berhasil menyimpan target bulanan untuk ${activeUser.user_name}.`);
      }
      
      setDrawerVisible(false);
      if (selectedYear && currentArea) {
        fetchData(selectedYear.year(), currentArea.id);
      }
    } catch (errInfo) {
      console.error('Save Monthly Failed:', errInfo);
      message.error('Gagal menyimpan target bulanan.');
    } finally {
      setSaving(false);
    }
  };

  const handleDrawerValuesChange = (changedValues: any, allValues: any) => {
    let sum = 0;
    for (let m = 1; m <= 12; m++) {
      sum += Number(allValues[`month_${m}`] || 0);
    }
    setTotalAllocated(sum);
  };

  const columns = [
    {
      title: 'Nama Sales',
      dataIndex: 'user_name',
      key: 'user_name',
      width: '45%',
    },
    {
      title: 'Target Leads',
      dataIndex: 'leads_target',
      key: 'leads_target',
      editable: true,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: 'Aksi',
      dataIndex: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Button
            onClick={() => toggleEdit(record.key)}
            icon={isEditing(record) ? <CloseOutlined /> : <EditOutlined />}
            size="small"
          >
            {isEditing(record) ? 'Batal' : 'Edit'}
          </Button>
          {!isEditing(record) && (
            <Button
              type="primary"
              ghost
              icon={<CalendarOutlined />}
              onClick={() => openMonthlyDrawer(record)}
              size="small"
            >
              Atur Bulanan
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const handleSubmitAll = async () => {
    if (editingKeys.size === 0) {
      message.info('Tidak ada data yang sedang diubah untuk disimpan.');
      return;
    }
    if (!areaTarget?.target_id) {
        message.error("ID Target Kawasan tidak ditemukan. Harap pastikan target kawasan sudah diisi terlebih dahulu.");
        return;
    }
    setSaving(true);
    try {
      const formValues = await form.validateFields();

      const payload = Array.from(editingKeys).map(key => {
        const item = userTargets.find(d => d.key === key)!;
        return {
          target_id: item.target_id,
          user_id: item.user_id,
          property_area_target_id: areaTarget.target_id,
          leads_target: formValues[key].leads_target,
        };
      });

      await api.post('/targets/user', payload);

      setEditingKeys(new Set());
      message.success(`Berhasil menyimpan target sales.`);
      if (selectedYear && currentArea) {
        fetchData(selectedYear.year(), currentArea.id);
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
      message.error('Gagal menyimpan, mohon periksa kembali semua isian Anda.');
    } finally {
      setSaving(false);
    }
  };

  const leadsDifference = (areaTarget?.leads_target || 0) - totalLeadsAssigned;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space direction="vertical" size={0}>
            <Space align='center'>
              <Title level={2} style={{ margin: 0 }}>
                Target Sales
              </Title>
              <DatePicker
                picker="year"
                value={selectedYear}
                onChange={(date) => setSelectedYear(date)}
                style={{ marginLeft: 16 }}
                allowClear={false}
              />
            </Space>
            <Paragraph type="secondary" style={{ margin: 0, marginTop: 4 }}>
                Total leads yang didistribusikan ke sales sebanyak{' '}
                <strong>{areaTarget ? totalLeadsAssigned.toLocaleString() : 0}</strong> dari total {' '}
                <strong>{(areaTarget?.leads_target || 0).toLocaleString()}</strong> leads yang dibutuhkan.
            </Paragraph>
          </Space>
        </Col>
      </Row>

      <Card>
        <Spin spinning={loading}>
          {!currentArea || !selectedYear ? (
            <Alert message="Silakan pilih Tahun untuk melihat atau mengisi target." type="info" showIcon />
          ) : !areaTarget ? (
             <Alert message="Target untuk kawasan ini belum diatur pada tahun yang dipilih. Harap isi Target Kawasan terlebih dahulu." type="warning" showIcon />
          ) : (
            <>
              {leadsDifference !== 0 && (
                 <Alert
                    message={leadsDifference > 0
                        ? `${leadsDifference.toLocaleString()} leads belum terdistribusi.`
                        : `${Math.abs(leadsDifference).toLocaleString()} leads melebihi target kawasan.`
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
              )}
              <Form form={form} component={false}>
                <Table
                  components={{ body: { cell: EditableCell } }}
                  bordered
                  dataSource={userTargets}
                  columns={mergedColumns.filter(c => c.dataIndex !== 'action' || canEdit)}
                  rowClassName="editable-row"
                  pagination={false}
                />
              </Form>
              {canEdit && (
                <Row justify="end" style={{ marginTop: 24 }}>
                  <Col>
                    <Button
                      type="primary"
                      onClick={handleSubmitAll}
                      loading={saving}
                      icon={<SaveOutlined />}
                      disabled={editingKeys.size === 0}
                    >
                      Kunci & Simpan Semua Target
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Spin>
      </Card>

      {/* --- Monthly Target Drawer --- */}
      <Drawer
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Target Bulanan - {activeUser?.user_name}</Title>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Target Tahunan: <strong>{activeUser?.leads_target || 0}</strong> Leads
            </Paragraph>
          </div>
        }
        width={500}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
        footer={
          <Row justify="space-between" align="middle" style={{ padding: '8px 4px' }}>
            <Col>
              <Space direction="vertical" size={2}>
                <Typography.Text type={totalAllocated > (activeUser?.leads_target || 0) ? "danger" : "secondary"}>
                  Total Alokasi: <strong>{totalAllocated}</strong> / {activeUser?.leads_target || 0} Leads
                </Typography.Text>
                {totalAllocated > (activeUser?.leads_target || 0) ? (
                  <Badge status="error" text="Melebihi target tahunan!" />
                ) : totalAllocated < (activeUser?.leads_target || 0) ? (
                  <Badge status="warning" text={`Sisa alokasi: ${(activeUser?.leads_target || 0) - totalAllocated} leads`} />
                ) : (
                  <Badge status="success" text="Teralokasi sempurna" />
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Button onClick={() => setDrawerVisible(false)}>Batal</Button>
                {canEdit && (
                  <Button
                    type="primary"
                    onClick={handleSaveMonthly}
                    loading={saving}
                    disabled={totalAllocated > (activeUser?.leads_target || 0)}
                  >
                    Simpan Bulanan
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        }
      >
        <Form form={drawerForm} layout="vertical" onValuesChange={handleDrawerValuesChange}>
          <Space direction="vertical" size={16} style={{ width: '100%', marginBottom: 24 }}>
            {!canEdit && (
              <Alert
                message="Mode Lihat Saja (Read-Only). Anda tidak memiliki izin untuk mengedit target."
                type="warning"
                showIcon
              />
            )}
            {canEdit && (
              <>
                <Alert
                  message="Gunakan tombol cepat di bawah ini untuk membagi target tahunan secara instan."
                  type="info"
                  showIcon
                />
                <Row gutter={12}>
                  <Col span={12}>
                    <Button block onClick={handleAutoDivideAll} disabled={!canEdit}>
                      Bagi Rata (12 Bulan)
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button block onClick={handleAutoDivideRemaining} disabled={!canEdit}>
                      Bagi Rata (Sisa Bulan)
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Space>

          <Row gutter={[16, 12]}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
              const monthLabel = dayjs().month(m - 1).format('MMMM');
              return (
                <Col span={12} key={m}>
                  <Form.Item
                    name={`month_${m}`}
                    label={`${m}. ${monthLabel}`}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true, message: 'Harus diisi' }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} precision={0} disabled={!canEdit} />
                  </Form.Item>
                </Col>
              );
            })}
          </Row>
        </Form>
      </Drawer>
    </div>
  );
};

export default TargetSalesPage;
